import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import {
  Calendar,
  RefreshCw,
  Plus,
  Pause,
  Play,
  X,
  MapPin,
  Home,
  Dog,
  Wrench,
  Sparkles,
} from "lucide-react";
import {
  DAYS,
  TIMES,
  FREQUENCIES,
  EQUIPMENT_OPTIONS,
  getStatusBadgeStyle,
} from "./utils";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const StatusBadge = ({ status }) => (
  <Badge className={`${getStatusBadgeStyle(status)} font-medium`}>
    {status}
  </Badge>
);

const initialForm = (profile) => ({
  service_id: "",
  service_name: "",
  address: profile?.address || "",
  postcode: profile?.postcode || "",
  bedrooms: 1,
  bathrooms: 1,
  preferred_day: "Monday",
  preferred_time: "09:00",
  frequency: "weekly",
  special_instructions: "",
  equipment_required: "full",
  has_pets: false,
  pet_details: "",
  access_instructions: "",
  parking_info: "",
  preferences: "",
  initial_deep_clean: true,
});

export const RecurringTab = ({
  recurringBookings,
  services,
  profile,
  onRefresh,
}) => {
  const [recurringModal, setRecurringModal] = useState(false);
  const [recurringForm, setRecurringForm] = useState(initialForm(profile));

  const handleServiceSelect = (serviceId) => {
    const service = services.find((s) => s.id === serviceId);
    setRecurringForm((prev) => ({
      ...prev,
      service_id: serviceId,
      service_name: service?.name || "",
    }));
  };

  const calculateRecurringPrice = () => {
    if (!recurringForm.service_id) return 0;
    const service = services.find((s) => s.id === recurringForm.service_id);
    if (!service) return 0;

    const totalRooms = recurringForm.bedrooms + recurringForm.bathrooms;
    let price = service.base_price + totalRooms * service.price_per_room;

    if (recurringForm.frequency === "weekly") price *= 0.85;
    else if (recurringForm.frequency === "fortnightly") price *= 0.9;
    else if (recurringForm.frequency === "monthly") price *= 0.95;

    return price.toFixed(2);
  };

  const handleCreateRecurring = async () => {
    if (
      !recurringForm.service_id ||
      !recurringForm.address ||
      !recurringForm.postcode
    ) {
      toast.error("Please fill in all required fields");
      return;
    }

    try {
      await axios.post(`${API}/customer/recurring`, recurringForm);
      toast.success("Recurring booking created!");
      setRecurringModal(false);
      setRecurringForm(initialForm(profile));
      onRefresh();
    } catch (error) {
      toast.error("Failed to create recurring booking");
    }
  };

  const handlePauseResume = async (recurringId, currentStatus) => {
    try {
      const newStatus = currentStatus === "active" ? "paused" : "active";
      await axios.patch(
        `${API}/customer/recurring/${recurringId}`,
        { status: newStatus }
      );
      toast.success(
        `Recurring booking ${newStatus === "active" ? "resumed" : "paused"}`
      );
      onRefresh();
    } catch (error) {
      toast.error("Failed to update recurring booking");
    }
  };

  const handleCancelRecurring = async (recurringId) => {
    if (!window.confirm("Are you sure you want to cancel this recurring booking?"))
      return;
    try {
      await axios.delete(
        `${API}/customer/recurring/${recurringId}`
      );
      toast.success("Recurring booking cancelled");
      onRefresh();
    } catch (error) {
      toast.error("Failed to cancel recurring booking");
    }
  };

  return (
    <Card className="bg-white border-[#E2E8E0]">
      <CardHeader className="flex flex-row items-center justify-between">
        <div>
          <CardTitle className="text-[#1A2E12]">Recurring Cleans</CardTitle>
          <p className="text-sm text-[#5C6B57] mt-1">
            Save up to 15% with regular bookings
          </p>
        </div>
        <Dialog open={recurringModal} onOpenChange={setRecurringModal}>
          <DialogTrigger asChild>
            <Button
              className="bg-[#2D5016] hover:bg-[#1A2E12]"
              onClick={() => setRecurringForm(initialForm(profile))}
              data-testid="setup-recurring-btn"
            >
              <Plus className="w-4 h-4 mr-2" />
              Set Up Recurring
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="text-[#1A2E12]">
                Set Up Recurring Clean
              </DialogTitle>
            </DialogHeader>
            <div className="space-y-6 mt-4">
              {/* Service */}
              <div className="bg-[#F9FAF9] rounded-xl p-4">
                <h3 className="font-semibold text-[#2D5016] mb-3 flex items-center gap-2">
                  <Sparkles className="w-4 h-4" /> Service Selection
                </h3>
                <Label className="text-sm text-[#5C6B57]">
                  Choose your service *
                </Label>
                <Select
                  value={recurringForm.service_id}
                  onValueChange={handleServiceSelect}
                >
                  <SelectTrigger
                    className="mt-1"
                    data-testid="recurring-service"
                  >
                    <SelectValue placeholder="Select a service" />
                  </SelectTrigger>
                  <SelectContent>
                    {services
                      .filter((s) => s.category === "residential")
                      .map((service) => (
                        <SelectItem key={service.id} value={service.id}>
                          {service.name} (from £{service.base_price})
                        </SelectItem>
                      ))}
                  </SelectContent>
                </Select>
              </div>

              {/* Property */}
              <div className="bg-[#F9FAF9] rounded-xl p-4">
                <h3 className="font-semibold text-[#2D5016] mb-3 flex items-center gap-2">
                  <Home className="w-4 h-4" /> Property Details
                </h3>
                <div className="space-y-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label className="text-sm text-[#5C6B57]">Bedrooms</Label>
                      <Select
                        value={String(recurringForm.bedrooms)}
                        onValueChange={(v) =>
                          setRecurringForm((prev) => ({
                            ...prev,
                            bedrooms: parseInt(v),
                          }))
                        }
                      >
                        <SelectTrigger className="mt-1">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {[1, 2, 3, 4, 5, 6].map((n) => (
                            <SelectItem key={n} value={String(n)}>
                              {n}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label className="text-sm text-[#5C6B57]">
                        Bathrooms
                      </Label>
                      <Select
                        value={String(recurringForm.bathrooms)}
                        onValueChange={(v) =>
                          setRecurringForm((prev) => ({
                            ...prev,
                            bathrooms: parseInt(v),
                          }))
                        }
                      >
                        <SelectTrigger className="mt-1">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {[1, 2, 3, 4, 5].map((n) => (
                            <SelectItem key={n} value={String(n)}>
                              {n}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  </div>

                  <div>
                    <Label className="text-sm text-[#5C6B57]">Address *</Label>
                    <Input
                      value={recurringForm.address}
                      onChange={(e) =>
                        setRecurringForm((prev) => ({
                          ...prev,
                          address: e.target.value,
                        }))
                      }
                      className="mt-1"
                      placeholder="123 Main Street, London"
                      data-testid="recurring-address"
                    />
                  </div>
                  <div>
                    <Label className="text-sm text-[#5C6B57]">Postcode *</Label>
                    <Input
                      value={recurringForm.postcode}
                      onChange={(e) =>
                        setRecurringForm((prev) => ({
                          ...prev,
                          postcode: e.target.value.toUpperCase(),
                        }))
                      }
                      className="mt-1"
                      placeholder="SW1A 1AA"
                      data-testid="recurring-postcode"
                    />
                  </div>
                  <div>
                    <Label className="text-sm text-[#5C6B57]">
                      Access Instructions
                    </Label>
                    <Input
                      value={recurringForm.access_instructions}
                      onChange={(e) =>
                        setRecurringForm((prev) => ({
                          ...prev,
                          access_instructions: e.target.value,
                        }))
                      }
                      className="mt-1"
                      placeholder="e.g., Key under mat, doorman, key safe code..."
                      data-testid="recurring-access"
                    />
                  </div>
                  <div>
                    <Label className="text-sm text-[#5C6B57]">
                      Parking Information
                    </Label>
                    <Input
                      value={recurringForm.parking_info}
                      onChange={(e) =>
                        setRecurringForm((prev) => ({
                          ...prev,
                          parking_info: e.target.value,
                        }))
                      }
                      className="mt-1"
                      placeholder="e.g., Free parking on street, permit required..."
                      data-testid="recurring-parking"
                    />
                  </div>
                </div>
              </div>

              {/* Equipment & Pets */}
              <div className="bg-[#F9FAF9] rounded-xl p-4">
                <h3 className="font-semibold text-[#2D5016] mb-3 flex items-center gap-2">
                  <Wrench className="w-4 h-4" /> Equipment & Pets
                </h3>
                <div className="space-y-4">
                  <div>
                    <Label className="text-sm text-[#5C6B57]">
                      Equipment Required
                    </Label>
                    <div className="grid grid-cols-1 gap-2 mt-2">
                      {EQUIPMENT_OPTIONS.map((option) => (
                        <button
                          key={option.value}
                          type="button"
                          onClick={() =>
                            setRecurringForm((prev) => ({
                              ...prev,
                              equipment_required: option.value,
                            }))
                          }
                          className={`p-3 rounded-lg border-2 text-left transition-all ${
                            recurringForm.equipment_required === option.value
                              ? "border-[#2D5016] bg-[#E8F5E9]"
                              : "border-[#E2E8E0] hover:border-[#7CB342]"
                          }`}
                          data-testid={`equipment-${option.value}`}
                        >
                          <p className="font-medium text-[#1A2E12] text-sm">
                            {option.label}
                          </p>
                          <p className="text-xs text-[#5C6B57]">
                            {option.description}
                          </p>
                        </button>
                      ))}
                    </div>
                  </div>

                  <div className="flex items-center justify-between p-3 bg-white rounded-lg border border-[#E2E8E0]">
                    <div className="flex items-center gap-3">
                      <Dog className="w-5 h-5 text-[#5C6B57]" />
                      <div>
                        <p className="font-medium text-[#1A2E12]">
                          Do you have pets?
                        </p>
                        <p className="text-xs text-[#5C6B57]">
                          Let us know so our team can prepare
                        </p>
                      </div>
                    </div>
                    <Switch
                      checked={recurringForm.has_pets}
                      onCheckedChange={(checked) =>
                        setRecurringForm((prev) => ({
                          ...prev,
                          has_pets: checked,
                        }))
                      }
                      data-testid="has-pets-switch"
                    />
                  </div>

                  {recurringForm.has_pets && (
                    <div>
                      <Label className="text-sm text-[#5C6B57]">
                        Pet Details
                      </Label>
                      <Textarea
                        value={recurringForm.pet_details}
                        onChange={(e) =>
                          setRecurringForm((prev) => ({
                            ...prev,
                            pet_details: e.target.value,
                          }))
                        }
                        className="mt-1"
                        placeholder="e.g., 2 cats, friendly dog named Max, birds in conservatory..."
                        rows={2}
                        data-testid="pet-details"
                      />
                    </div>
                  )}
                </div>
              </div>

              {/* Schedule */}
              <div className="bg-[#F9FAF9] rounded-xl p-4">
                <h3 className="font-semibold text-[#2D5016] mb-3 flex items-center gap-2">
                  <Calendar className="w-4 h-4" /> Schedule
                </h3>
                <div className="space-y-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label className="text-sm text-[#5C6B57]">
                        Preferred Day
                      </Label>
                      <Select
                        value={recurringForm.preferred_day}
                        onValueChange={(v) =>
                          setRecurringForm((prev) => ({
                            ...prev,
                            preferred_day: v,
                          }))
                        }
                      >
                        <SelectTrigger className="mt-1">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {DAYS.map((day) => (
                            <SelectItem key={day} value={day}>
                              {day}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label className="text-sm text-[#5C6B57]">
                        Preferred Time
                      </Label>
                      <Select
                        value={recurringForm.preferred_time}
                        onValueChange={(v) =>
                          setRecurringForm((prev) => ({
                            ...prev,
                            preferred_time: v,
                          }))
                        }
                      >
                        <SelectTrigger className="mt-1">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {TIMES.map((time) => (
                            <SelectItem key={time} value={time}>
                              {time}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  </div>

                  <div>
                    <Label className="text-sm text-[#5C6B57]">Frequency</Label>
                    <div className="grid grid-cols-3 gap-2 mt-2">
                      {FREQUENCIES.map((freq) => (
                        <button
                          key={freq.value}
                          type="button"
                          onClick={() =>
                            setRecurringForm((prev) => ({
                              ...prev,
                              frequency: freq.value,
                            }))
                          }
                          className={`p-3 rounded-lg border-2 text-center transition-all ${
                            recurringForm.frequency === freq.value
                              ? "border-[#2D5016] bg-[#E8F5E9]"
                              : "border-[#E2E8E0] hover:border-[#7CB342]"
                          }`}
                          data-testid={`frequency-${freq.value}`}
                        >
                          <p className="font-medium text-[#1A2E12] text-sm capitalize">
                            {freq.value}
                          </p>
                          <p className="text-xs text-[#7CB342]">
                            {freq.discount} off
                          </p>
                        </button>
                      ))}
                    </div>
                  </div>

                  <div className="flex items-center justify-between p-3 bg-white rounded-lg border border-[#E2E8E0]">
                    <div>
                      <p className="font-medium text-[#1A2E12]">
                        Initial Deep Clean
                      </p>
                      <p className="text-xs text-[#5C6B57]">
                        First clean is a thorough deep clean
                      </p>
                    </div>
                    <Switch
                      checked={recurringForm.initial_deep_clean}
                      onCheckedChange={(checked) =>
                        setRecurringForm((prev) => ({
                          ...prev,
                          initial_deep_clean: checked,
                        }))
                      }
                      data-testid="initial-deep-clean-switch"
                    />
                  </div>
                </div>
              </div>

              {/* Preferences */}
              <div className="bg-[#F9FAF9] rounded-xl p-4">
                <h3 className="font-semibold text-[#2D5016] mb-3">
                  Preferences & Instructions
                </h3>
                <div className="space-y-4">
                  <div>
                    <Label className="text-sm text-[#5C6B57]">
                      Cleaning Preferences
                    </Label>
                    <Textarea
                      value={recurringForm.preferences}
                      onChange={(e) =>
                        setRecurringForm((prev) => ({
                          ...prev,
                          preferences: e.target.value,
                        }))
                      }
                      className="mt-1"
                      placeholder="e.g., Focus on kitchen, use unscented products, don't enter office room..."
                      rows={2}
                      data-testid="preferences"
                    />
                  </div>
                  <div>
                    <Label className="text-sm text-[#5C6B57]">
                      Special Instructions
                    </Label>
                    <Textarea
                      value={recurringForm.special_instructions}
                      onChange={(e) =>
                        setRecurringForm((prev) => ({
                          ...prev,
                          special_instructions: e.target.value,
                        }))
                      }
                      className="mt-1"
                      placeholder="Any other special requests or notes for our cleaning team..."
                      rows={2}
                      data-testid="special-instructions"
                    />
                  </div>
                </div>
              </div>

              {recurringForm.service_id && (
                <div className="bg-[#2D5016] rounded-xl p-4 text-white">
                  <div className="flex justify-between items-center">
                    <div>
                      <p className="text-sm opacity-80">Price per clean</p>
                      <p className="text-xs opacity-60">
                        {
                          FREQUENCIES.find(
                            (f) => f.value === recurringForm.frequency
                          )?.discount
                        }{" "}
                        discount applied
                      </p>
                    </div>
                    <span className="text-3xl font-bold">
                      £{calculateRecurringPrice()}
                    </span>
                  </div>
                </div>
              )}

              <Button
                onClick={handleCreateRecurring}
                className="w-full bg-[#2D5016] hover:bg-[#1A2E12] rounded-full py-6 text-lg"
                data-testid="create-recurring-submit"
              >
                <RefreshCw className="w-5 h-5 mr-2" />
                Set Up Recurring Clean
              </Button>
            </div>
          </DialogContent>
        </Dialog>
      </CardHeader>
      <CardContent>
        {recurringBookings.length === 0 ? (
          <div className="text-center py-12">
            <RefreshCw className="w-12 h-12 text-[#E2E8E0] mx-auto mb-4" />
            <p className="text-[#5C6B57] mb-2">No recurring bookings yet</p>
            <p className="text-sm text-[#5C6B57] mb-4">
              Save up to 15% with regular bookings
            </p>
          </div>
        ) : (
          <div className="space-y-4">
            {recurringBookings.map((recurring) => (
              <div
                key={recurring.id}
                className="border border-[#E2E8E0] rounded-xl p-4 hover:shadow-md transition-shadow"
              >
                <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                  <div className="flex-1">
                    <div className="flex items-center gap-3 mb-2">
                      <h3 className="font-semibold text-[#1A2E12]">
                        {recurring.service_name}
                      </h3>
                      <StatusBadge status={recurring.status} />
                      <Badge className="bg-[#E8F5E9] text-[#2D5016]">
                        {recurring.frequency}
                      </Badge>
                    </div>
                    <div className="flex flex-wrap gap-4 text-sm text-[#5C6B57]">
                      <span className="flex items-center gap-1">
                        <Calendar className="w-4 h-4" />
                        Every {recurring.preferred_day} at{" "}
                        {recurring.preferred_time}
                      </span>
                      <span className="flex items-center gap-1">
                        <MapPin className="w-4 h-4" />
                        {recurring.postcode}
                      </span>
                      <span className="flex items-center gap-1">
                        <Home className="w-4 h-4" />
                        {recurring.bedrooms} bed, {recurring.bathrooms} bath
                      </span>
                    </div>
                  </div>
                  <div className="flex items-center gap-4">
                    <div className="text-right">
                      <p className="text-2xl font-bold text-[#2D5016]">
                        £{recurring.total_price.toFixed(2)}
                      </p>
                      <p className="text-xs text-[#5C6B57]">per clean</p>
                    </div>
                    <div className="flex gap-2">
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() =>
                          handlePauseResume(recurring.id, recurring.status)
                        }
                        disabled={recurring.status === "cancelled"}
                      >
                        {recurring.status === "active" ? (
                          <Pause className="w-4 h-4" />
                        ) : (
                          <Play className="w-4 h-4" />
                        )}
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        className="text-red-500"
                        onClick={() => handleCancelRecurring(recurring.id)}
                        disabled={recurring.status === "cancelled"}
                      >
                        <X className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
};
